import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Slider,
  InputAdornment,
  IconButton,
  Chip,
} from '@mui/material';
import { Calculate, Refresh, Edit, Save, ContentCopy } from '@mui/icons-material';

const PriceCalculator = () => {
  // Calculator States
  const [basePrice, setBasePrice] = useState(1000);
  const [urgencyPercent, setUrgencyPercent] = useState(20);
  const [discountPercent, setDiscountPercent] = useState(10);
  const [profitMargin, setProfitMargin] = useState(15);
  const [editingPrice, setEditingPrice] = useState(false);
  const [copied, setCopied] = useState(false);

  const calculatePrice = () => {
    const urgencyAmount = (basePrice * urgencyPercent) / 100;
    const subtotal = basePrice + urgencyAmount;
    const profitAmount = (subtotal * profitMargin) / 100;
    const totalBeforeDiscount = subtotal + profitAmount;
    const discountAmount = (totalBeforeDiscount * discountPercent) / 100;
    const finalPrice = totalBeforeDiscount - discountAmount;
    
    return {
      basePrice: parseFloat(basePrice).toFixed(2),
      urgencyAmount: urgencyAmount.toFixed(2),
      urgencyPercent,
      subtotal: subtotal.toFixed(2),
      profitAmount: profitAmount.toFixed(2),
      profitMargin,
      totalBeforeDiscount: totalBeforeDiscount.toFixed(2),
      discountAmount: discountAmount.toFixed(2),
      discountPercent,
      finalPrice: finalPrice.toFixed(2),
    };
  };

  const handleReset = () => {
    setBasePrice(1000);
    setUrgencyPercent(20);
    setDiscountPercent(10);
    setProfitMargin(15);
    setEditingPrice(false);
  };

  const handleCopyResult = () => {
    const result = calculatePrice();
    const text = `
Price Calculation Summary
========================
Base Price: $${result.basePrice}
Urgency Surcharge (${urgencyPercent}%): +$${result.urgencyAmount}
Subtotal: $${result.subtotal}
Profit Margin (${profitMargin}%): +$${result.profitAmount}
Total Before Discount: $${result.totalBeforeDiscount}
Discount (${discountPercent}%): -$${result.discountAmount}
========================
FINAL PRICE: $${result.finalPrice}
    `;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const priceResult = calculatePrice();

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Calculate fontSize="large" color="primary" />
          Price Calculator & Adjustment Tool
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Calculate and adjust package prices with customizable percentages for urgency, profit margin, and discounts.
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>How to use:</strong> Enter a base price, adjust the percentage sliders, and view the calculated final price. 
        This tool helps you create competitive pricing for customized packages.
      </Alert>

      <Grid container spacing={3}>
        {/* Left Side - Input Controls */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
              Pricing Parameters
            </Typography>

            {/* Base Price Input */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Base Package Price
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={basePrice}
                  onChange={(e) => setBasePrice(parseFloat(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton 
                          size="small" 
                          onClick={() => setEditingPrice(!editingPrice)}
                          color={editingPrice ? 'primary' : 'default'}
                        >
                          {editingPrice ? <Save /> : <Edit />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  disabled={!editingPrice}
                  helperText="Starting price before adjustments"
                />
              </CardContent>
            </Card>

            {/* Urgency Surcharge */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Urgency Surcharge
                  </Typography>
                  <Chip label={`${urgencyPercent}%`} color="warning" />
                </Box>
                <Slider
                  value={urgencyPercent}
                  onChange={(e, newValue) => setUrgencyPercent(newValue)}
                  min={0}
                  max={50}
                  step={1}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 25, label: '25%' },
                    { value: 50, label: '50%' },
                  ]}
                  valueLabelDisplay="auto"
                  color="warning"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  💡 Applied for last-minute bookings (0-30 days before departure)
                </Typography>
                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                  Amount: +${priceResult.urgencyAmount}
                </Typography>
              </CardContent>
            </Card>

            {/* Profit Margin */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Profit Margin
                  </Typography>
                  <Chip label={`${profitMargin}%`} color="success" />
                </Box>
                <Slider
                  value={profitMargin}
                  onChange={(e, newValue) => setProfitMargin(newValue)}
                  min={0}
                  max={40}
                  step={1}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 20, label: '20%' },
                    { value: 40, label: '40%' },
                  ]}
                  valueLabelDisplay="auto"
                  color="success"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  💼 Company profit percentage
                </Typography>
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  Amount: +${priceResult.profitAmount}
                </Typography>
              </CardContent>
            </Card>

            {/* Discount */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Customer Discount
                  </Typography>
                  <Chip label={`${discountPercent}%`} color="error" />
                </Box>
                <Slider
                  value={discountPercent}
                  onChange={(e, newValue) => setDiscountPercent(newValue)}
                  min={0}
                  max={50}
                  step={1}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 25, label: '25%' },
                    { value: 50, label: '50%' },
                  ]}
                  valueLabelDisplay="auto"
                  color="error"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  🎁 Promotional or loyalty discount
                </Typography>
                <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                  Amount: -${priceResult.discountAmount}
                </Typography>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleReset}
                fullWidth
              >
                Reset to Default
              </Button>
              <Button
                variant="contained"
                startIcon={<ContentCopy />}
                onClick={handleCopyResult}
                fullWidth
                color={copied ? 'success' : 'primary'}
              >
                {copied ? 'Copied!' : 'Copy Results'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Right Side - Calculation Results */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3, height: '100%', bgcolor: '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'secondary.main', mb: 3 }}>
              Calculated Price Breakdown
            </Typography>

            <Card sx={{ mb: 3, border: '2px solid', borderColor: 'secondary.main' }}>
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      {/* Base Price */}
                      <TableRow>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            Base Price
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="h6" fontWeight="bold">
                            ${priceResult.basePrice}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Urgency Surcharge */}
                      <TableRow sx={{ bgcolor: '#fff3e0' }}>
                        <TableCell>
                          <Typography variant="body2" color="warning.main">
                            + Urgency Surcharge ({urgencyPercent}%)
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body1" color="warning.main" fontWeight="medium">
                            +${priceResult.urgencyAmount}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Subtotal */}
                      <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            Subtotal
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="h6" fontWeight="bold" color="primary">
                            ${priceResult.subtotal}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Profit Margin */}
                      <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                        <TableCell>
                          <Typography variant="body2" color="success.main">
                            + Profit Margin ({profitMargin}%)
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body1" color="success.main" fontWeight="medium">
                            +${priceResult.profitAmount}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Total Before Discount */}
                      <TableRow sx={{ bgcolor: '#f3e5f5' }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            Total Before Discount
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="h6" fontWeight="bold" color="secondary">
                            ${priceResult.totalBeforeDiscount}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Discount */}
                      <TableRow sx={{ bgcolor: '#ffebee' }}>
                        <TableCell>
                          <Typography variant="body2" color="error.main">
                            - Discount ({discountPercent}%)
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body1" color="error.main" fontWeight="medium">
                            -${priceResult.discountAmount}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Divider */}
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Divider sx={{ my: 1 }} />
                        </TableCell>
                      </TableRow>

                      {/* Final Price */}
                      <TableRow sx={{ bgcolor: 'secondary.light' }}>
                        <TableCell>
                          <Typography variant="h6" fontWeight="bold" color="secondary.contrastText">
                            FINAL PRICE
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="h4" fontWeight="bold" color="secondary.main">
                            ${priceResult.finalPrice}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            {/* Quick Presets */}
            <Card sx={{ bgcolor: 'white' }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Quick Presets
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setUrgencyPercent(30);
                      setDiscountPercent(0);
                      setProfitMargin(20);
                    }}
                  >
                    Last Minute (High Urgency)
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setUrgencyPercent(0);
                      setDiscountPercent(20);
                      setProfitMargin(15);
                    }}
                  >
                    Early Bird (High Discount)
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setUrgencyPercent(10);
                      setDiscountPercent(10);
                      setProfitMargin(15);
                    }}
                  >
                    Standard Package
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setUrgencyPercent(0);
                      setDiscountPercent(0);
                      setProfitMargin(25);
                    }}
                  >
                    Premium (Max Profit)
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Usage Tips */}
            <Alert severity="success" sx={{ mt: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                💡 Usage Tips
              </Typography>
              <Typography variant="caption" component="div">
                • Use higher urgency % for bookings within 30 days<br />
                • Apply discounts for group bookings or loyal customers<br />
                • Adjust profit margin based on season and demand<br />
                • Copy results to share with team or customers
              </Typography>
            </Alert>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PriceCalculator;
